import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { MultiSelect } from '@creditornot/cb-select';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import {
  BulkEditPayload,
  Category,
  MandatoryWarning,
  MenuContainer,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WoltModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { reportBulkAction } from 'modules/menu/behavioral';
import { mandatoryWarningsForCountry, mandatoryWarningTranslationMap } from 'modules/menu/mandatoryWarnings';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  mode: 'add' | 'remove';
  onClearSelection: () => void;
  onClose: () => void;
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  background-color: ${cssThemedValue('surfaceMain')};
  padding: 0;
  border-radius: 8px;
  overflow-y: visible;
`;

const Footer = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const MandatoryWarningsModal = ({ categories, menuId, mode, onClearSelection, onClose, selectedIds }: Props) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();
  const { updateMenuData } = useMenuData();
  const { country } = useContext(MenuLocaleContext);

  const [selectedWarnings, setSelectedWarnings] = useState<MandatoryWarning[]>([]);

  const mandatoryWarningOptions: { value: MandatoryWarning; label: string }[] = useMemo(() => {
    return (
      mandatoryWarningsForCountry[country]?.map((warningForCountry: MandatoryWarning) => ({
        value: warningForCountry,
        label: intl.formatMessage({ id: mandatoryWarningTranslationMap[warningForCountry] }),
      })) || []
    );
  }, [country, intl]);

  const handleSubmit = useMutation(
    () => {
      const payload: BulkEditPayload = {
        ...buildBulkActionBasePayload(categories, selectedIds),
        ...(mode === 'add'
          ? {
              add_mandatory_warnings: selectedWarnings,
            }
          : {
              remove_mandatory_warnings: selectedWarnings,
            }),
      };

      return bulkEditItems(menuId, payload);
    },
    {
      onMutate: () => {
        reportBulkAction(selectedIds.size, 'BULK_ADD_MANDATORY_WARNINGS');
      },
      onSuccess: (data: MenuContainer) => {
        updateMenuData({ actionType: 'updateMenuContainer', payload: data });
        onClearSelection();
        toast.success(
          formatPluralMessage({
            id:
              mode === 'add'
                ? 'menu.bulkActions.mandatoryWarnings.addSuccessDescription'
                : 'menu.bulkActions.mandatoryWarnings.removeSuccessDescription',
            count: selectedWarnings.length,
          }),
        );
      },
      onError: () => {
        toast.error(
          formatPluralMessage({
            id:
              mode === 'add'
                ? 'menu.bulkActions.mandatoryWarnings.addErrorDescription'
                : 'menu.bulkActions.mandatoryWarnings.removeErrorDescription',
            count: selectedWarnings.length,
          }),
          {
            duration: Infinity,
          },
        );
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={
            mode === 'add' ? (
              <FormattedMessage id="menu.bulkActions.mandatoryWarnings.addMandatoryWarnings" />
            ) : (
              <FormattedMessage id="menu.bulkActions.mandatoryWarnings.removeMandatoryWarnings" />
            )
          }
        />
      </ModalHeader>
      <ModalContent style={{ padding: 0 }}>
        <FormRow horizontalPadding label={intl.formatMessage({ id: 'menu.mandatoryWarnings.title' })}>
          <MultiSelect
            options={mandatoryWarningOptions}
            value={selectedWarnings}
            onChange={(values: MandatoryWarning[]) => setSelectedWarnings(values)}
          />
          {mode === 'remove' && (
            <InputAlert style={{ marginBlockStart: '8px' }} variant="info">
              <FormattedMessage id="menu.bulkActions.mandatoryWarnings.removeMandatoryWarningsInfo" />
            </InputAlert>
          )}
        </FormRow>
      </ModalContent>
      <Footer>
        <CancelButton onClick={onClose}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <SubmitButton
          disabled={selectedWarnings.length === 0}
          loading={handleSubmit.isLoading}
          type="submit"
          onClick={() => handleSubmit.mutate()}
        >
          <FormattedMessage id="state.save" />
        </SubmitButton>
      </Footer>
    </Root>
  );
};

export default MandatoryWarningsModal;
